import styled from "styled-components";
import theme from "../../style/theme";
import {RoutingType} from "../../types/routing-service/routing-request";
import {isStartOrPickUp, RoutingResponse, VehicleResult,} from "../../types/routing-service/routing-response";
import RoutingIcon from "../icons/RoutingIcon";
import TimeFillIcon from "../icons/TimeFillIcon";
import RouteArrowIcon from "../icons/RouteArrowIcon";
import ErrorWarningFillIcon from "../icons/ErrorWarningFillIcon";
import TriangleArrowRightIcon from "../icons/TriangleArrowRightIcon";
import UserFillIcon from "../icons/UserFillIcon";
import RouteResultCircleBlueIcon from "../icons/RouteResultCircleBlueIcon";
import RouteResultCircleYellowIcon from "../icons/RouteResultCircleYellowIcon";
import {utils, writeFile} from 'xlsx';
import {datetimeFormat} from "../../utils/date/date-format";
import {ContainedGrayButton} from "../button/ContainedGrayButton";
import {TaskStatus} from "../../types/routing-service/task-status";

interface Props {
    result?: RoutingResponse;
}

export const RoutingServiceResult: React.FC<Props> = ({result}) => {
    const actionType = {
        START: "출발",
        PICK_UP: "상차",
        DROP_OFF: "전달",
        END: "종료",
    };

    const optimizationType = {
        MIN_DISTANCE: "거리 최적화",
        TIME_EQUAL: "시간 최적화",
        TASK_EQUAL: "공동 분배 최적화",
    };

    const handleButtonClick = (url: string) => {
        window.open(url, '_blank');
    };

    const convertToExcel = (data: RoutingResponse) => {
        const wb = utils.book_new();

        const allData = [
            ['전체 요약'],
            ['총 소요시간', `${Math.floor(data.routingSummary.basicInfo.totalTime / 3600)}시간 ${Math.floor((data.routingSummary.basicInfo.totalTime % 3600) / 60)}분`],
            ['작업시간', `${Math.floor(data.routingSummary.basicInfo.workTime / 3600)}시간 ${Math.floor((data.routingSummary.basicInfo.workTime % 3600) / 60)}분`],
            ['이동시간', `${Math.floor(data.routingSummary.basicInfo.travelTime / 3600)}시간 ${Math.floor((data.routingSummary.basicInfo.travelTime % 3600) / 60)}분`],
            ['총 이동거리', `${Math.floor(data.routingSummary.basicInfo.distance / 1000)}km`],
            ['배송 물류', `${data.routingSummary.basicInfo.numTask}건`],
        ];

        if (data.routingSummary.unassignedTasks.length > 0) {
            allData.push([''], ['미배정 물류']);
            allData.push(['물류 #', '상차 주소지', '하차 주소지']);
            data.routingSummary.unassignedTasks.forEach(task => {
                allData.push([task.taskName, task.pickUpPlace.placeName, task.dropOffPlace.placeName]);
            });
        }

        data.vehicleResults.forEach((vehicleResult, index) => {
            allData.push([''], [`배송원 ${index + 1} - 배송 요약`]);
            allData.push(
                ['총 소요시간', `${Math.floor(vehicleResult.basicInfo.totalTime / 3600)}시간 ${Math.floor((vehicleResult.basicInfo.totalTime % 3600) / 60)}분`],
                ['작업시간', `${Math.floor(vehicleResult.basicInfo.workTime / 3600)}시간 ${Math.floor((vehicleResult.basicInfo.workTime % 3600) / 60)}분`],
                ['이동시간', `${Math.floor(vehicleResult.basicInfo.travelTime / 3600)}시간 ${Math.floor((vehicleResult.basicInfo.travelTime % 3600) / 60)}분`],
                ['총 이동거리', `${Math.floor(vehicleResult.basicInfo.distance / 1000)}km`],
                ['배송 물류', `${vehicleResult.basicInfo.numTask}건`]
            );

            if (vehicleResult.polyline) {
                allData.push(['경로 링크', `https://valhalla.github.io/demos/polyline/?unescape=true&polyline6=false#${vehicleResult.polyline}`])
            }

            allData.push([`<경유지 상세>`]);
            allData.push(['물류 이름', '물류 타입', '실제 도착시간', '지연 여부', '주소지', '소요시간', '이전 장소와의 거리', '이전 장소로부터 이동시간']);
            vehicleResult.taskSequences.forEach(taskSequence => {
                const arrivalTime = new Date(taskSequence.arrivalTime);
                allData.push([
                    taskSequence.taskName,
                    actionType[taskSequence.taskType],
                    `${arrivalTime.getHours()}시 ${arrivalTime.getMinutes()}분`,
                    taskSequence.status == TaskStatus.Delayed ? '도착 지연' : '범위 내 도착',
                    taskSequence.placeName,
                    `${Math.floor(taskSequence.serviceTime / 60)}분`,
                    `${taskSequence.distance / 1000}km`,
                    `${Math.round(taskSequence.intervalTime / 60)}분`,
                ]);
            });
        });

        const ws = utils.aoa_to_sheet(allData);

        utils.book_append_sheet(wb, ws, '동선 최적화 결과');

        writeFile(wb, `옹고잉 동선 최적화 결과_${datetimeFormat(new Date())}.xlsx`);
    };

    const formatTime = (time: number) => {
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        if (hours > 0 && minutes > 0) {
            return `${hours}시간 ${minutes}분`;
        } else if (hours > 0) {
            return `${hours}시간`;
        } else {
            return `${minutes}분`;
        }
    };

    return (
        <Container>
            {result && (
                <>
                    <RoutingServiceSummaryContainer>
                        <RoutingServiceIconTitleRow>
                            <RoutingIcon/>
                            <RoutingServiceSummaryTitleLabel
                                color={theme.palette.primitives.blue500}>
                                {`${
                                    result?.routingSummary.routingType ===
                                    RoutingType.PickupAndDelivery
                                        ? `최적 배차(${
                                            optimizationType[result.routingSummary.optimizationType]
                                        })`
                                        : "단일 배송원 시간 측정"
                                }으로 최적화한 결과입니다.`}
                            </RoutingServiceSummaryTitleLabel>
                        </RoutingServiceIconTitleRow>
                        <RoutingSummarySectionContainer>
                            <RoutingSummarySection>
                                <RoutingSummarySectionTitleRow>
                                    <TimeFillIcon
                                        color={theme.palette.primitives.gray500}
                                        size={16}
                                    />
                                    <RoutingSummarySectionTitleLabel>
                                        총 소요시간
                                    </RoutingSummarySectionTitleLabel>
                                </RoutingSummarySectionTitleRow>
                                <RoutingSummarySectionMainLabel>
                                    {`${formatTime(result.routingSummary.basicInfo.totalTime)}`}
                                </RoutingSummarySectionMainLabel>

                                <RoutingSummarySectionDescLabel>
                                    {`작업시간 ${formatTime(result.routingSummary.basicInfo.workTime)}`}
                                    {'\n'}
                                    {`이동시간 ${formatTime(result.routingSummary.basicInfo.travelTime)}`}
                                </RoutingSummarySectionDescLabel>
                            </RoutingSummarySection>
                            <RoutingSummarySection>
                                <RoutingSummarySectionTitleRow>
                                    <RouteArrowIcon
                                        color={theme.palette.primitives.gray500}
                                        size={16}
                                    />
                                    <RoutingSummarySectionTitleLabel>
                                        총 이동거리
                                    </RoutingSummarySectionTitleLabel>
                                </RoutingSummarySectionTitleRow>
                                <RoutingSummarySectionMainLabel>
                                    {`${Math.floor(
                                        result.routingSummary.basicInfo.distance / 1000
                                    )}km`}
                                </RoutingSummarySectionMainLabel>
                                <RoutingSummarySectionDescLabel>
                                    {`배송 물류 ${result.routingSummary.basicInfo.numTask}건`}
                                </RoutingSummarySectionDescLabel>
                            </RoutingSummarySection>
                        </RoutingSummarySectionContainer>
                    </RoutingServiceSummaryContainer>

                    {result.routingSummary.unassignedTasks.length > 0 && (
                        <RoutingServiceUnassignedContainer>
                            <RoutingServiceUnassignedTitleRow>
                                <ErrorWarningFillIcon
                                    width={"16"}
                                    height={"16"}
                                    fill={theme.palette.primitives.red600}
                                />
                                <RoutingServiceSummaryTitleLabel
                                    color={theme.palette.primitives.red600}>
                                    미배정(시간 내 배송 불가) 물류가 있습니다.
                                </RoutingServiceSummaryTitleLabel>
                            </RoutingServiceUnassignedTitleRow>
                            <RoutingServiceUnassignedItemsWrapper>
                                {result.routingSummary.unassignedTasks.map((task, index) => {
                                    return (
                                        <RoutingServiceUnassignedItemContainer>
                                            <RoutingServiceUnassignedDeliveryLabel>
                                                {task.taskName}
                                            </RoutingServiceUnassignedDeliveryLabel>
                                            <RoutingServiceUnassignedDeliveryAddressWrapper>
                                                <RoutingServiceUnassignedDeliveryAddressLabel>
                                                    {task.pickUpPlace.placeName}
                                                </RoutingServiceUnassignedDeliveryAddressLabel>
                                                <TriangleArrowRightIcon
                                                    size={24}
                                                    color={theme.palette.primitives.gray500}/>
                                                <RoutingServiceUnassignedDeliveryAddressLabel>
                                                    {task.dropOffPlace.placeName}
                                                </RoutingServiceUnassignedDeliveryAddressLabel>
                                            </RoutingServiceUnassignedDeliveryAddressWrapper>
                                        </RoutingServiceUnassignedItemContainer>
                                    );
                                })}
                            </RoutingServiceUnassignedItemsWrapper>

                            <RoutingServiceUnassignedDescLabel>
                                배송원 근무 시간을 늘리거나 배송원을 더 배정해보세요
                            </RoutingServiceUnassignedDescLabel>
                        </RoutingServiceUnassignedContainer>
                    )}

                    <RoutingServiceResultContentContainer>
                        {result.vehicleResults.map(
                            (vehicleResult: VehicleResult, index) => (
                                <RoutingServiceResultDriverContainer>
                                    <RoutingServiceResultDriverContentHeaderRow>
                                        <UserFillIcon
                                            size={24}
                                            color={theme.palette.primitives.gray300}/>
                                        <RoutingServiceResultDriverContentHeaderLabel>
                                            {vehicleResult.vehicleName || "배송원"}
                                        </RoutingServiceResultDriverContentHeaderLabel>
                                        {vehicleResult.polyline && (
                                            <RoutingServiceResultRouteButton
                                                onClick={() => handleButtonClick(`https://valhalla.github.io/demos/polyline/?unescape=true&polyline6=false#${vehicleResult.polyline}`)}>
                                                경로 보기
                                            </RoutingServiceResultRouteButton>
                                        )}
                                    </RoutingServiceResultDriverContentHeaderRow>

                                    <RoutingSummarySectionContainer className={"margin"}>
                                        <RoutingSummarySection>
                                            <RoutingSummarySectionTitleRow>
                                                <TimeFillIcon
                                                    color={theme.palette.primitives.gray500}
                                                    size={16}
                                                />
                                                <RoutingSummarySectionTitleLabel>
                                                    총 소요시간
                                                </RoutingSummarySectionTitleLabel>
                                            </RoutingSummarySectionTitleRow>
                                            <RoutingSummarySectionMainLabel>
                                                {`${formatTime(vehicleResult.basicInfo.totalTime)}`}
                                            </RoutingSummarySectionMainLabel>

                                            <RoutingSummarySectionDescLabel>
                                                {`작업시간 ${formatTime(vehicleResult.basicInfo.workTime)}`}
                                                {'\n'}
                                                {`이동시간 ${formatTime(vehicleResult.basicInfo.travelTime)}`}
                                            </RoutingSummarySectionDescLabel>
                                        </RoutingSummarySection>
                                        <RoutingSummarySection>
                                            <RoutingSummarySectionTitleRow>
                                                <RouteArrowIcon
                                                    color={theme.palette.primitives.gray500}
                                                    size={16}
                                                />
                                                <RoutingSummarySectionTitleLabel>
                                                    총 이동거리
                                                </RoutingSummarySectionTitleLabel>
                                            </RoutingSummarySectionTitleRow>
                                            <RoutingSummarySectionMainLabel>
                                                {`${Math.floor(
                                                    vehicleResult.basicInfo.distance / 1000
                                                )}km`}
                                            </RoutingSummarySectionMainLabel>
                                            <RoutingSummarySectionDescLabel>
                                                {`배송 물류 ${vehicleResult.basicInfo.numTask}건`}
                                            </RoutingSummarySectionDescLabel>
                                        </RoutingSummarySection>
                                    </RoutingSummarySectionContainer>

                                    <RoutingServiceResultItemWrapper>
                                        {vehicleResult.taskSequences.reduce<JSX.Element[]>((acc, taskSequence, index) => {
                                            const arrivalTime = new Date(taskSequence.arrivalTime);
                                            const previousTaskSequence = index > 0 ? vehicleResult.taskSequences[index - 1] : null;

                                            const isSameAddress = previousTaskSequence && taskSequence.placeName === previousTaskSequence.placeName;

                                            acc.push(
                                                <RoutingServiceResultItem
                                                    key={index}
                                                    color={isStartOrPickUp(taskSequence.taskType) ? theme.palette.primitives.blue50 : theme.palette.primitives.yellow50}>
                                                    <RoutingServiceResultItemHeaderRow>
                                                        {isStartOrPickUp(taskSequence.taskType) ?
                                                            <RouteResultCircleBlueIcon/> :
                                                            <RouteResultCircleYellowIcon/>}
                                                        <RoutingServiceResultItemHeaderLabel
                                                            color={isStartOrPickUp(taskSequence.taskType) ? theme.palette.primitives.blue500 : theme.palette.primitives.yellow500}>
                                                            {`${taskSequence.taskName} ${actionType[taskSequence.taskType]}`}
                                                        </RoutingServiceResultItemHeaderLabel>
                                                        <RoutingServiceResultItemHeaderTimeLabel>
                                                            {`${arrivalTime.getHours()}시 ${arrivalTime.getMinutes()}분`}
                                                        </RoutingServiceResultItemHeaderTimeLabel>
                                                        {taskSequence.status === TaskStatus.Delayed && (
                                                            <RoutingServiceDelayedLabel>
                                                                도착 지연
                                                            </RoutingServiceDelayedLabel>)
                                                        }
                                                    </RoutingServiceResultItemHeaderRow>
                                                    <RoutingServiceResultItemAddressLabel>
                                                        {taskSequence.placeName}
                                                        {isSameAddress && (
                                                            <RoutingServiceResultItemBadge>
                                                                이전과 동일한 주소
                                                            </RoutingServiceResultItemBadge>
                                                        )}
                                                    </RoutingServiceResultItemAddressLabel>
                                                    {taskSequence.serviceTime > 0 && (
                                                        <RoutingServiceResultItemDescContainer>
                                                            <RoutingServiceResultItemDescLabel>
                                                                작업 소요시간
                                                            </RoutingServiceResultItemDescLabel>
                                                            <RoutingServiceResultItemResultLabel>
                                                                {`${Math.floor(taskSequence.serviceTime / 60)}분`}
                                                            </RoutingServiceResultItemResultLabel>
                                                        </RoutingServiceResultItemDescContainer>
                                                    )}
                                                    {taskSequence.distance > 0 && (
                                                        <RoutingServiceResultItemDescContainer>
                                                            <RoutingServiceResultItemDescLabel>
                                                                이전 장소와의 거리
                                                            </RoutingServiceResultItemDescLabel>
                                                            <RoutingServiceResultItemResultLabel>
                                                                {`${taskSequence.distance / 1000}km`}
                                                            </RoutingServiceResultItemResultLabel>
                                                        </RoutingServiceResultItemDescContainer>
                                                    )}
                                                    {taskSequence.intervalTime > 0 && (
                                                        <RoutingServiceResultItemDescContainer>
                                                            <RoutingServiceResultItemDescLabel>
                                                                이전 장소로부터 이동시간
                                                            </RoutingServiceResultItemDescLabel>
                                                            <RoutingServiceResultItemResultLabel>
                                                                {`${Math.round(taskSequence.intervalTime / 60)}분`}
                                                            </RoutingServiceResultItemResultLabel>
                                                        </RoutingServiceResultItemDescContainer>
                                                    )}
                                                </RoutingServiceResultItem>
                                            );

                                            return acc;
                                        }, [])}
                                    </RoutingServiceResultItemWrapper>

                                </RoutingServiceResultDriverContainer>
                            ))}
                        <ContainedGrayButton width={"100"} height={"62"} text={"결과 엑셀 다운로드"}
                                             onClick={() => convertToExcel(result)}/>
                    </RoutingServiceResultContentContainer>
                </>
            )
            }

            <RoutingServiceBody>
                {!result && (
                    <>
                        <EmptyContentHeader>아직 표시할 결과가 없습니다</EmptyContentHeader>
                        <EmptyContentBody>
                            결과를 보려면 왼쪽에서 결과보기를 먼저 눌러주세요
                        </EmptyContentBody>
                    </>
                )}
            </RoutingServiceBody>
        </Container>
    )
        ;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 24%;
  height: 100%;
  flex-grow: 1;
  border-left: 1px solid ${
          theme.palette.primitives.gray200
  };
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  white-space: nowrap;
`;

const RoutingServiceSummaryContainer = styled.div`
  background: ${
          theme.palette.primitives.gray50
  };
  padding: 18px 32px;
`

const RoutingServiceIconTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const RoutingServiceSummaryTitleLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${
          (props) => props.color || theme.palette.primitives.blue500
  };
  margin: 0 0 0 2px;
`

const RoutingSummarySectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: auto;
  margin-top: 14px;
  flex-grow: 1;
  gap: 10px;
  justify-content: space-around;

  &.margin {
    margin: 24px;
  }
`;

const RoutingSummarySection = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  border: 0.5px solid ${
          theme.palette.primitives.gray100
  };
  padding: 16px;
  flex-grow: 1;
  align-items: center;
  width: 100%;
  gap: 6px;
`;

const RoutingSummarySectionTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

const RoutingSummarySectionTitleLabel = styled.div`
  font-weight: 400;
  font-size: 13px;
  color: ${
          theme.palette.primitives.gray600
  };
  text-align: center;
`;

const RoutingSummarySectionMainLabel = styled.div`
  font-weight: 700;
  font-size: 22px;
  color: ${
          theme.palette.primitives.gray800
  };
  text-align: center;
`;

const RoutingSummarySectionDescLabel = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: ${
          theme.palette.primitives.gray400
  };
  white-space: pre-wrap;
  text-align: center;
`;

const RoutingServiceUnassignedContainer = styled.div`
  background: ${
          theme.palette.primitives.red50
  };
`

const RoutingServiceUnassignedTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 16px 0 0 32px;
`

const RoutingServiceUnassignedItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 16px;
`

const RoutingServiceUnassignedItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  background: ${
          theme.palette.primitives.red100
  };
  padding: 10px 32px;
  align-items: center;
`

const RoutingServiceUnassignedDeliveryLabel = styled.div`
  width: 80px;
  min-width: 80px;
  font-size: 13px;
  font-weight: 400;
  color: ${
          theme.palette.primitives.white
  };
  white-space: pre-wrap;
  text-align: center;
  align-items: center;
  background: ${
          theme.palette.primitives.red500
  };
  padding: 6px 0;
  border-radius: 999px;
`

const RoutingServiceUnassignedDeliveryAddressWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  margin-left: 16px;
`

const RoutingServiceUnassignedDeliveryAddressLabel = styled.div`
  width: 50%;
  font-size: 13px;
  font-weight: 600;
  color: ${
          theme.palette.primitives.gray700
  };
`

const RoutingServiceUnassignedDescLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${
          theme.palette.primitives.gray400
  };
  padding: 12px 32px;
`

const RoutingServiceResultContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 18px 32px;
  gap: 16px;
`

const RoutingServiceResultDriverContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: ${
          theme.palette.primitives.gray200
  } 1px solid;
  border-radius: 8px;
  overflow: hidden;
`

const RoutingServiceResultDriverContentHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 24px 0 24px;
`

const RoutingServiceResultDriverContentHeaderLabel = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: ${
          theme.palette.primitives.gray900
  };
  margin-left: 8px;
`

const RoutingServiceResultRouteButton = styled.button`
  font-size: 12px;
  font-weight: 400;
  color: ${
          theme.palette.primitives.blue500
  };
  margin-left: 8px;
  padding: 6px 10px;
  cursor: pointer;
  background: transparent;
  border: ${
          theme.palette.primitives.gray100
  } 0.5px solid;
  border-radius: 4px;

  &:hover {
    background: ${theme.palette.primitives.gray50};
  }
`

const RoutingServiceResultItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const RoutingServiceResultItem = styled.div`
  background: ${
          ({color}) => color || theme.palette.primitives.blue50
  };
  padding: 20px 33px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const RoutingServiceResultItemHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

const RoutingServiceResultItemHeaderLabel = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: ${
          ({color}) => color || theme.palette.primitives.blue500
  };
`

const RoutingServiceResultItemHeaderTimeLabel = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: ${
          theme.palette.primitives.gray500
  };
`

const RoutingServiceDelayedLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: ${theme.palette.primitives.red400};
`

const RoutingServiceResultItemAddressLabel = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 16px;
  font-weight: 400;
  color: ${
          theme.palette.primitives.gray700
  };
  margin: 4px 0;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  word-break: break-word;
  white-space: normal;
`

const RoutingServiceResultItemBadge = styled.div`
  font-size: 9px;
  font-weight: 300;
  background-color: ${theme.palette.primitives.gray100};
  border-radius: 8px;
  padding: 2px 4px;
  color: ${theme.palette.primitives.gray500};
`

const RoutingServiceResultItemDescContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

const RoutingServiceResultItemDescLabel = styled.p`
  font-size: 14px;
  font-weight: 300;
  color: ${
          theme.palette.primitives.gray400
  };
`

const RoutingServiceResultItemResultLabel = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${
          theme.palette.primitives.gray700
  };
`

const RoutingServiceBody = styled.div`
  color: #000000;
  height: 50px;
  font-weight: 700;
  line-height: normal;
  padding: 21px 49px 0 32px;
  margin: 0 0 20px 0;
`;

const EmptyContentHeader = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: ${
          theme.palette.primitives.gray500
  };
`;

const EmptyContentBody = styled.div`
  font-size: 14px;
  font-weight: 300;
  color: ${
          theme.palette.primitives.gray300
  };
`;
